import { useCallback, useEffect, useState } from 'react';

import { applyPalette, loadPalette } from '../lib/palette.js';

export type Theme = 'light' | 'dark';

const KEY = 'erp-theme';

function load(): Theme {
  try {
    const v = window.localStorage.getItem(KEY);
    if (v === 'dark' || v === 'light') return v;
  } catch {
    /* ignore */
  }
  if (typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'light') {
    return 'light';
  }
  return 'dark';
}

/** Set <html data-theme> + .dark class and re-apply the active palette for the theme. */
export function applyTheme(theme: Theme): void {
  if (typeof document === 'undefined') return;
  const el = document.documentElement;
  el.setAttribute('data-theme', theme);
  el.classList.toggle('dark', theme === 'dark');
  applyPalette(loadPalette(), theme === 'dark');
}

/**
 * User-scoped light/dark preference, persisted to localStorage so it survives
 * tab restarts. Mirrors useNumerals. Toggling re-skins the brand for the theme
 * (dark needs a luminous brand) via applyPalette.
 */
export function useTheme(): { theme: Theme; setTheme: (t: Theme) => void; toggle: () => void } {
  const [theme, setState] = useState<Theme>(load());

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  useEffect(() => {
    function onStorage(e: StorageEvent): void {
      if (e.key === KEY) setState(load());
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setTheme = useCallback((t: Theme): void => {
    try {
      window.localStorage.setItem(KEY, t);
    } catch {
      /* ignore */
    }
    setState(t);
  }, []);

  const toggle = useCallback(() => setState((cur) => {
    const next: Theme = cur === 'dark' ? 'light' : 'dark';
    try { window.localStorage.setItem(KEY, next); } catch { /* ignore */ }
    return next;
  }), []);

  return { theme, setTheme, toggle };
}
